import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  QueryList,
  SimpleChanges
} from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Subscribable } from '@csspension/base-angular';
import { ScreenService } from '../../../../services/screen-service.service';

@Component({ template: '' })
export abstract class InvestmentInstructionsVisualizationComponent
  extends Subscribable
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input() backgroundColor: string = '#FFFFFF';
  @Input() borderColor: string = '#CACACA';
  @Input() label: string = '';

  // When multiple related visualization components are displayed together, the parent component should set
  // `forcedColumns` to the length of the longest `investmentInstructions` array among them. This ensures that
  // each component uses a consistent column count, maintaining visual alignment and sizing across all views.
  @Input() forcedColumns: number = 0;

  private screenService = inject(ScreenService);

  readonly COLUMN_MIN_WIDTH = 150;
  readonly DESKTOP_GAP_PX = 16;
  readonly MOBILE_GAP_PX = 8;
  readonly MAX_ALLOWED_COLUMNS = 4;

  protected observer!: ResizeObserver;

  abstract gridElements: QueryList<ElementRef>;

  public isMobile$ = this.screenService.isMobile$;

  protected _forcedColumns = new BehaviorSubject(1);
  public forcedColumns$ = this._forcedColumns as Observable<number>;

  protected _gridWidth = new BehaviorSubject(0);
  public gridWidth$ = this._gridWidth as Observable<number>;

  public gridStyles$ = combineLatest([this.forcedColumns$, this.gridWidth$, this.isMobile$]).pipe(
    map(([forcedCols, gridWidth, isMobile]) => {
      const gap = isMobile ? this.MOBILE_GAP_PX : this.DESKTOP_GAP_PX;
      const maxColumnsFit = Math.floor(gridWidth / (this.COLUMN_MIN_WIDTH + gap));
      const nColumnsToUse = Math.min(maxColumnsFit, forcedCols, this.MAX_ALLOWED_COLUMNS);

      const gridTemplateColumns = `repeat(${nColumnsToUse}, minmax(${this.COLUMN_MIN_WIDTH}px, 1fr))`;
      return {
        display: 'grid',
        'grid-template-columns': gridTemplateColumns,
        gap: `${gap}px`
      };
    })
  );

  public ngAfterViewInit(): void {
    this.initializeObserver();

    this.gridElements.changes.subscribeWhileAlive({
      owner: this,
      next: () => {
        this.initializeObserver();
      }
    });
  }

  private initializeObserver(): void {
    this.observer?.disconnect();
    this.observer = new ResizeObserver(() => this.updateGridWidth());
    if (this.gridElements.length > 0) {
      this.observer.observe(this.gridElements.first.nativeElement);
    }
  }

  protected updateGridWidth(): void {
    if (this.gridElements.length > 0) {
      const gridWidth: number = this.gridElements.first.nativeElement.clientWidth;
      this._gridWidth.next(gridWidth);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.forcedColumns) {
      this._forcedColumns.next(this.forcedColumns);
    }
  }

  public ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}
